export const ADMIN_TIERS = {
  SUPER_ADMIN: "super_admin", // Full system access
  ADMIN: "admin", // General admin access
  SUPPORT: "support", // Customer support access
  CONTENT: "content", // Content management only
};

export const TIER_DEFAULT_PERMISSIONS = {
  [ADMIN_TIERS.ADMIN]: [
    "users:view",
    "users:manage",
    "meetings:view",
    "meetings:manage",
    "meetings:delete",
    "meetings:resend_emails",
    "meetings:change_status",
    "moderation:view",
    "moderation:resolve",
    "moderation:assign",
    "services:view",
    "services:manage",
    "content:manage",
    "support:manage",
    "analytics:view",
  ],
  [ADMIN_TIERS.SUPPORT]: [
    "users:view",
    "meetings:view",
    "meetings:resend_emails",
    "services:view",
    "support:manage",
  ],
  [ADMIN_TIERS.CONTENT]: ["content:manage"],
  // SUPER_ADMIN: gets all permissions automatically via hasPermission
};

export function hasPermission(session, permission) {
  // Super admins have all permissions
  if (session?.user?.tier?.admin_tier === ADMIN_TIERS.SUPER_ADMIN) return true;
  if (session?.user?.permissions?.includes("all")) return true;
  // Check explicit permissions
  if (session?.user?.permissions?.includes(permission)) return true;
  // Check tier default permissions
  const tier = session?.user?.tier?.admin_tier;
  if (tier && TIER_DEFAULT_PERMISSIONS[tier]?.includes(permission)) return true;
  return false;
}

export function hasAdminRole(session) {
  return Boolean(session?.user?.tier?.admin_tier);
}

export function getAdminRole(session) {
  return session?.user?.tier?.admin_tier || null;
}

/**
 * Common permission check functions
 */
export function canManageUsers(session) {
  return hasPermission(session, "users:manage");
}

export function canViewUsers(session) {
  return (
    hasPermission(session, "users:view") ||
    hasPermission(session, "users:manage")
  );
}

export function canImpersonateUsers(session) {
  return hasPermission(session, "users:impersonate");
}

export function canManageContent(session) {
  return hasPermission(session, "content:manage");
}

export function canManageSupport(session) {
  return hasPermission(session, "support:manage");
}

export function canConfigureSystem(session) {
  return hasPermission(session, "system:configure");
}

/**
 * Meeting management permission functions
 */
export function canViewMeetings(session) {
  return hasPermission(session, "meetings:view");
}

export function canManageMeetings(session) {
  return hasPermission(session, "meetings:manage");
}

export function canResendMeetingEmails(session) {
  return hasPermission(session, "meetings:resend_emails");
}

export function canDeleteMeetings(session) {
  return hasPermission(session, "meetings:delete");
}

export function canChangeMeetingStatus(session) {
  return hasPermission(session, "meetings:change_status");
}

export function isSuperAdmin(session) {
  return session?.user?.tier?.admin_tier === ADMIN_TIERS.SUPER_ADMIN;
}

/**
 * Moderation permission functions
 */
export function canViewModeration(session) {
  return hasPermission(session, "moderation:view");
}

export function canViewServices(session) {
  return hasPermission(session, "services:view");
}

export function canManageServices(session) {
  return hasPermission(session, "services:manage");
}

export function canResolveModeration(session) {
  return hasPermission(session, "moderation:resolve");
}

/**
 * Analytics dashboard (PostHog KPIs + embedded dashboards).
 * Available to super admins and general admins by default.
 */
export function canViewAnalytics(session) {
  return hasPermission(session, "analytics:view");
}

export function canAssignModeration(session) {
  return hasPermission(session, "moderation:assign");
}

export function canManageModeration(session) {
  return hasPermission(session, "moderation:manage");
}

export function canApproveMeetingEmails(session) {
  return (
    hasPermission(session, "moderation:resolve") ||
    hasPermission(session, "moderation:manage")
  );
}
